import { applyErrorDiagnostics } from '@flashka/shared/observability';

/**
 * Context the web app adds to every Sentry event that survived the filters
 * (`beforeSendFilter`), so a report names its cause instead of only its
 * symptom. Each piece answers the question a parked issue could not:
 *
 * - shared upload / `.apkg` extractor context (`applyErrorDiagnostics`) —
 *   FLASHKA-VITE-2WR, -1ZS;
 * - the editor a ProseMirror RangeError came from — FLASHKA-VITE-2W1;
 * - the IndexedDB stores the code expects vs the ones on disk — -2M6;
 * - the third-party scripts on the page for a rejection with no value — -2D4;
 * - the PostHog session, so the report links to the recording of it.
 *
 * Enrichment never drops or breaks an event: each piece is guarded.
 */

type Exception = { type?: string; value?: string };
type EnrichableEvent = {
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  exception?: { values?: Exception[] };
};

type DexieLike = {
  name: string;
  verno: number;
  tables: { name: string }[];
  backendDB(): { objectStoreNames: ArrayLike<string> } | null | undefined;
};

type DiagnosticsSources = {
  /** The open per-user database, registered by `database/db.ts`. */
  indexedDb?: () => DexieLike | null | undefined;
  /** The PostHog session id and replay URL, registered at Sentry init. */
  posthog?: () => { sessionId?: string; replayUrl?: string } | null;
};

const sources: DiagnosticsSources = {};

export function registerDiagnosticsSource(source: DiagnosticsSources): void {
  Object.assign(sources, source);
}

const PROSEMIRROR_RANGE =
  /Position -?\d+ out of range|outside of fragment|mismatched transaction|Invalid content for node/;
const NON_ERROR_REJECTION = /^Non-Error promise rejection captured with value: (.*)$/;
const MISSING_STORE = /object stores? (was|were) not found/i;

export function enrichEvent<E extends EnrichableEvent>(
  event: E,
  hint: { originalException?: unknown } | undefined,
  doc: Document | undefined = typeof document === 'undefined' ? undefined : document,
): E {
  guarded(() => applyErrorDiagnostics(event, hint));
  const values = event.exception?.values ?? [];
  const add = (tags: Record<string, string>, extra: Record<string, unknown>) => {
    event.tags = { ...tags, ...event.tags };
    event.extra = { ...extra, ...event.extra };
  };

  if (doc && values.some((v) => PROSEMIRROR_RANGE.test(v.value ?? ''))) {
    guarded(() => {
      const found = editorDiagnostics(doc, values);
      if (found) add(found.tags, { editor: found.extra });
    });
  }

  if (
    values.some((v) => v.type === 'NotFoundError' && MISSING_STORE.test(v.value ?? ''))
  ) {
    guarded(() => {
      const found = indexedDbDiagnostics(sources.indexedDb?.());
      if (found) add(found.tags, { indexedDb: found.extra });
    });
  }

  const rejection = values
    .map((v) => NON_ERROR_REJECTION.exec(v.value ?? ''))
    .find((m) => m !== null);
  if (doc && rejection) {
    guarded(() =>
      add(
        { 'rejection.value': rejection[1].slice(0, 40) },
        { thirdPartyScripts: thirdPartyScriptOrigins(doc) },
      ),
    );
  }

  guarded(() => {
    const session = sources.posthog?.();
    if (session?.sessionId) {
      add({ posthog_session: session.sessionId }, { posthogReplay: session.replayUrl });
    }
  });

  return event;
}

function guarded(run: () => void): void {
  try {
    run();
  } catch {
    // A diagnostic must never cost the report itself.
  }
}

type TiptapLike = {
  isEditable?: boolean;
  state?: {
    doc?: { content?: { size?: number }; childCount?: number };
    selection?: { from?: number; to?: number };
  };
  extensionManager?: { extensions?: { name?: string }[] };
};

/**
 * The editor a ProseMirror RangeError most likely came from — the focused one,
 * else the only one on the page — read from the element Tiptap tags with its
 * instance (`dom.editor`). The position in the message against the document's
 * size says whether a stale position outlived an edit.
 */
export function editorDiagnostics(
  doc: Document,
  values: Exception[],
): { tags: Record<string, string>; extra: Record<string, unknown> } | null {
  const all = Array.from(doc.querySelectorAll('.ProseMirror'));
  const focused = doc.activeElement?.closest?.('.ProseMirror') ?? null;
  const element = focused ?? (all.length === 1 ? all[0] : null);
  const editor = (element as (Element & { editor?: TiptapLike }) | null)?.editor;
  const position = values
    .map((v) => /Position (-?\d+)/.exec(v.value ?? '')?.[1])
    .find((p) => p !== undefined);
  const docSize = editor?.state?.doc?.content?.size;
  const tags: Record<string, string> = {
    'editor.found': editor ? (focused ? 'focused' : 'only') : 'none',
    'editor.count': String(all.length),
  };
  if (position !== undefined && docSize !== undefined) {
    tags['editor.position_past_end'] = Number(position) > docSize ? 'yes' : 'no';
  }
  if (!editor) return { tags, extra: { editorsOnPage: all.length } };
  return {
    tags,
    extra: {
      docSize,
      topLevelNodes: editor.state?.doc?.childCount,
      selection: editor.state?.selection
        ? { from: editor.state.selection.from, to: editor.state.selection.to }
        : null,
      editable: editor.isEditable,
      extensions: (editor.extensionManager?.extensions ?? [])
        .map((e) => e.name)
        .filter(Boolean)
        .slice(0, 60),
    },
  };
}

/** The stores this build declares vs the ones in the database on disk. */
export function indexedDbDiagnostics(
  db: DexieLike | null | undefined,
): { tags: Record<string, string>; extra: Record<string, unknown> } | null {
  if (!db) return null;
  const declared = db.tables.map((t) => t.name);
  const backend = db.backendDB();
  const onDisk = backend ? Array.from(backend.objectStoreNames) : null;
  const missing = onDisk ? declared.filter((name) => !onDisk.includes(name)) : null;
  return {
    tags: {
      'idb.version': String(db.verno),
      'idb.open': backend ? 'yes' : 'no',
      ...(missing ? { 'idb.missing_stores': String(missing.length) } : {}),
    },
    extra: { name: db.name.replace(/-[^-]+$/, '-<user>'), declared, onDisk, missing },
  };
}

/** Script origins on the page other than our own, for an error with no frame. */
export function thirdPartyScriptOrigins(doc: Document): string[] {
  const own = doc.location?.origin;
  const origins = new Set<string>();
  for (const script of Array.from(doc.scripts)) {
    if (!script.src) continue;
    try {
      const origin = new URL(script.src, own).origin;
      if (origin !== own) origins.add(origin);
    } catch {
      // not a URL
    }
  }
  return Array.from(origins).slice(0, 15);
}
